import React, { FC, useCallback } from 'react'
import { useLanguage } from '@moonpig/web-core-stores'
import { Box } from '@moonpig/launchpad-components'
import { getFormattedVariantSelectorPriceFields } from '../../utils/formatProductVariants/getFormattedVariantSelectorPriceFields'
import {
  type ModuleVariants,
  type ModuleContext,
  ModuleVariantsType,
} from '../types'
import { useProductStore } from '../../contexts/product'
import { formatProductVariants } from '../../utils/formatProductVariants/formatProductVariants'
import { VariantSelector } from '../../components/VariantSelector/VariantSelector'
import { DropdownWithIcon } from '../../components/DropdownWithIcon'
import { useTracking } from '../../contexts/tracking'
import { getVariantIcon } from '../../utils/getVariantIcon'
import { getVariantSelectorTitle } from '../../utils/getVariantSelectorTitle'
import { getVariantDescription } from '../../utils/getVariantDescription'
import { PriceVariantSelector } from '../../components/VariantSelectorPrice/PriceVariantSelector'
import { formatPriceWithoutDecimals } from '../../utils/formatPriceWithoutDecimals'
import { E_CARD_SIZE_ID } from '../../constants'
import { useProductsLocaleText } from '../../locale'

type Props = {
  module: ModuleVariants
  context: ModuleContext
}

export const VariantsModule: FC<Props> = ({
  module: { type, unavailableVariants, displayOptions },
}) => {
  const language = useLanguage()
  const localiseText = useProductsLocaleText()
  const [{ product, variant }, dispatcher] = useProductStore()
  const { selectProduct } = useTracking()

  const { variants, category } = product

  const handleVariantChange = useCallback(
    (variantKey: string) => {
      const selectedVariant =
        variants.find(v => v.sku === variantKey || v.key === variantKey) ||
        /* istanbul ignore next */ variant

      dispatcher.selectVariant(variantKey)
      selectProduct({
        product,
        variant: selectedVariant,
        index: 0,
        component: `size selector ${type}`,
      })
    },
    [variants, variant, dispatcher, selectProduct, product, type],
  )

  const eCardPlacement = displayOptions?.eCardPlacement

  const filteredVariants = variants
    .filter(v => !unavailableVariants?.includes(v.key))
    .sort((a, b) => {
      if (!eCardPlacement) return 0
      if (a.key === E_CARD_SIZE_ID) {
        return eCardPlacement === 'last' ? 1 : -1
      }
      if (b.key === E_CARD_SIZE_ID) {
        return eCardPlacement === 'last' ? -1 : 1
      }
      return 0
    })

  const formattedVariants = formatProductVariants({
    variants: filteredVariants,
    selectedVariant: variant,
    id: product.id,
    language,
    eCardDescription: localiseText('common.sent_instantly_via_email'),
  })

  const dropdownItems = formattedVariants.map(formattedVariant => ({
    value: formattedVariant.key,
    title: formattedVariant.title,
    icon: getVariantIcon({
      sizeId: formattedVariant.key,
      isLandscape: !!product.isLandscape,
    }),
    subtitle: `${formattedVariant.price.currencySymbol}${formattedVariant.price.displayPrice}`,
  }))

  const variantItems = formattedVariants.map(formattedVariant => ({
    ...formattedVariant,
    description: getVariantDescription(formattedVariant, localiseText),
    ...getFormattedVariantSelectorPriceFields(formattedVariant, language),
  }))

  return (
    <Box data-testid="module-variants" mb={6}>
      {type === ModuleVariantsType.GRID && (
        <PriceVariantSelector
          onChange={handleVariantChange}
          variants={variants
            .filter(v => v.sku !== null)
            .map(v => {
              return {
                sku: v.sku ?? /* istanbul ignore next */ '',
                price: formatPriceWithoutDecimals(
                  Math.abs(v.price.centAmount),
                  v.price.fractionDigits,
                  v.price.currencyCode,
                  1,
                  language,
                ),
              }
            })}
          selectedValue=""
        />
      )}
      {type === ModuleVariantsType.DROPDOWN && (
        <DropdownWithIcon
          title={localiseText('common.size')}
          titlePosition={displayOptions?.dropdownTitlePosition}
          titleTypography={displayOptions?.titleTypography}
          titleColor={displayOptions?.titleColor}
          color={displayOptions?.color}
          showPriceSuffix={displayOptions?.showPriceSuffix}
          selectedValue={variant.key}
          items={dropdownItems}
          onChange={handleVariantChange}
        />
      )}
      {type === ModuleVariantsType.RADIO && (
        <VariantSelector
          title={getVariantSelectorTitle(category.department, localiseText)}
          isLandscape={!!product.isLandscape}
          selectedValue={variant.key}
          items={variantItems}
          onChange={handleVariantChange}
        />
      )}
    </Box>
  )
}
